<template>
  <div class="safedig-professionals">
    <SafeDigHeader />
    <div class="siteSidebarAndContent siteSidebarAndContentWhiteBg">
      <div class="content contentNoPadding">
        <div class="homeContainer">
          <div class="homeTopContainer">
            <div class="homeTopInnerContainer">
              <div class="homeTopStatementContainer homeTopStatementSafedigindianaContainer">
                <div class="safedigCardCarousel">
                  <!-- Hero carousel -->
                  <section class="safedigHeroCarousel">
                    <div
                      v-for="(slide, index) in heroSlides"
                      :key="index"
                      class="safedigProfessionalsHero safedigHeroSlide"
                      :class="{ safedigHeroSlideActive: currentSlide === index }"
                      :style="{ backgroundImage: `url(${slide.image})` }"
                    >
                      <div class="safedigProfessionalsHeroOverlay">
                        <h1 class="safedigProfessionalsHeroTitle">{{ $t(slide.titleKey) }}</h1>
                        <p class="safedigProfessionalsHeroSubtitle">{{ $t(slide.subtitleKey) }}</p>
                        <router-link :to="slide.ctaTo" class="safedigHeroCtaLink">
                          <span class="safedigHeroCta">
                            {{ currentSlide === 0 ? heroPrimaryCtaText() : $t(slide.ctaTextKey) }}
                          </span>
                        </router-link>
                      </div>
                    </div>
                    <div class="safedigHeroDots">
                      <button
                        v-for="(slide, index) in heroSlides"
                        :key="index"
                        type="button"
                        class="safedigHeroDot"
                        :class="{ active: currentSlide === index }"
                        :aria-label="'Slide ' + (index + 1)"
                        @click="currentSlide = index"
                      />
                    </div>
                  </section>
                  <!-- Helpful Links Section -->
                  <div class="safedigHelpfulLinksWrapper">
                    <h2 class="safedigHelpfulLinksHeader">{{ $t('homepage.helpfulLinks') }}</h2>
                    <div class="safedigHelpfulLinksBand"></div>
                    <div class="safedigHelpfulLinksCardsRow">
                      <div class="safedigHelpfulLinksCard">
                        <img
                          src="../../../assets/safe-dig/notify811_holyMoley_scaled.png"
                          width="100%"
                          :alt="$t('professionalsPage.requestDigTicket')"
                        >
                        <div class="cardInfoBox">
                          <h2>{{ $t('professionalsPage.requestDigTicket') }}</h2>
                          <span>{{ $t('professionalsPage.requestDigTicketDescription') }}</span>
                          <br>
                          <div class="buttonContainer">
                            <a href="https://indiana811.org/pros/" target="_blank" rel="noopener noreferrer">
                              <button type="button" class="safedigButton">
                                {{ $t('professionalsPage.onlineTicketEntry') }}
                              </button>
                            </a>
                          </div>
                        </div>
                      </div>
                      <div class="safedigHelpfulLinksCard safedigCardLeftMargin">
                        <img
                          src="../../../assets/safe-dig/map_cropped_scaled.png"
                          width="100%"
                          :alt="$t('homepage.pipelineDamagesMap')"
                        >
                        <div class="cardInfoBox">
                          <h2>{{ $t('homepage.pipelineDamagesMap') }}</h2>
                          <span>{{ $t('homepage.pipelineDamagesMapDescription') }}</span>
                          <br>
                          <div class="buttonContainer">
                            <a
                              href="https://www.in.gov/iurc/pipeline-safety-division/damage-to-underground-facilities/indiana-pipeline-damages-map/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <button type="button" class="safedigButton">{{ $t('homepage.useTheMap') }}</button>
                            </a>
                          </div>
                        </div>
                      </div>
                      <div class="safedigHelpfulLinksCard safedigCardLeftMargin">
                        <img
                          src="../../../assets/safe-dig/calendar_cropped_scaled.png"
                          width="100%"
                          :alt="$t('homepage.eventsCalendar')"
                        >
                        <div class="cardInfoBox">
                          <h2>{{ $t('homepage.eventsCalendar') }}</h2>
                          <span>{{ $t('homepage.eventsCalendarDescription') }}</span>
                          <br>
                          <div class="buttonContainer">
                            <router-link to="/info/events">
                              <button type="button" class="safedigButton">{{ $t('homepage.upcomingEvents') }}</button>
                            </router-link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Footer (same as SafeDigIndianaPage) -->
    <div class="siteFooter footerOffWhite safedigFooter safedigFooterMargin safedigLanding">
      <div class="safedigCustomFooter">
        <div><img src="../../../assets/safe-dig/safedig_logo_grayscale-01.png" class="safedigindiana811Logo" alt="Safe Dig Indiana"></div>
        <p class="copyright">
          {{ $t('footer.copyright', { year: new Date().getFullYear() }) }}<br>
          {{ $t('footer.allRightsReserved') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import SafeDigHeader from '@/components/safe-dig/Header.vue';

// Same image for slide 1 placeholder – replace with excavator/hero image when ready
const heroSlide1Image = require('@/assets/safe-dig/welcome_prof.png');
const heroSlide2Image = require('@/assets/safe-dig/uppa_cropped_scaled.png');

export default {
  name: 'SafeDigProfessional',
  components: {
    SafeDigHeader
  },
  data() {
    return {
      currentSlide: 0,
      heroSlides: [
        {
          image: heroSlide1Image,
          titleKey: 'professionalsPage.heroWelcomeTitle',
          subtitleKey: 'professionalsPage.heroWelcomeSubtitle',
          ctaTextKey: 'professionalsPage.loginRegister',
          ctaTo: '/login'
        },
        {
          image: heroSlide2Image,
          titleKey: 'grants.heroTitle',
          subtitleKey: 'grants.heroSubtitle',
          ctaTextKey: 'professionalsPage.learnMore',
          ctaTo: '/info/grants'
        }
      ],
      carouselTimer: null,
      isAuthenticated: false
    };
  },
  mounted() {
    const token = localStorage.getItem('hot-token');
    this.isAuthenticated = !!token;
    this.startCarousel();
  },
  beforeDestroy() {
    this.stopCarousel();
  },
  methods: {
    heroPrimaryCtaText() {
      return this.isAuthenticated
        ? this.$t('professionalsPage.viewCourse')
        : this.$t('professionalsPage.loginRegister');
    },
    startCarousel() {
      this.carouselTimer = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.heroSlides.length;
      }, 6000);
    },
    stopCarousel() {
      if (this.carouselTimer) {
        clearInterval(this.carouselTimer);
        this.carouselTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.safedig-professionals :deep(.siteSidebarAndContentWhiteBg) {
  background: #fff;
}

.safedig-professionals :deep(.siteSidebarAndContent) {
  display: flex;
  flex: 1 0 auto;
}

.safedig-professionals :deep(.contentNoPadding) {
  padding: 0;
}

.safedig-professionals :deep(.content) {
  flex: 1;
  min-width: 2px;
  padding: 26px;
}

.homeContainer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.homeTopContainer {
  margin: auto;
  max-width: 1200px;
  width: 100%;
}

.homeTopInnerContainer {
  align-items: center;
  display: flex;
}

.homeTopStatementContainer {
  flex: 1;
  padding: 0 19px;
}

.safedigCardCarousel {
  position: relative;
  text-align: center;
  width: 100%;
}

/* Hero carousel */
.safedigHeroCarousel {
  position: relative;
  width: 100%;
  min-height: 465px;
}

.safedigHeroSlide {
  position: absolute;
  inset: 0;
  width: 100%;
  min-height: 465px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s ease;
}

.safedigHeroSlide.safedigHeroSlideActive {
  position: relative;
  opacity: 1;
  pointer-events: auto;
  z-index: 1;
}

.safedigProfessionalsHeroOverlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  background-color: rgba(0, 0, 0, 0.4);
}

.safedigHeroDots {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 10px;
  z-index: 2;
}

.safedigHeroDot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;
}

.safedigHeroDot:hover {
  background: rgba(255, 255, 255, 0.5);
}

.safedigHeroDot.active {
  background: #fff;
}

.safedigProfessionalsHeroTitle {
  font-size: 60px;
  line-height: 1.5;
  font-weight: 700;
  color: #fff;
  margin: 0;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.safedigProfessionalsHeroSubtitle {
  font-size: 20px;
  padding: 16px 14%;
  color: #fff;
  margin: 0;
  text-align: center;
  line-height: 22px;
}

.safedigHeroCtaLink {
  text-decoration: none;
  margin-top: 0.5rem;
}

.safedigHeroCta {
  display: inline-block;
  padding: 8px 20px;
  background: #edcd00;
  color: #2d4b6c;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 14px;
  transition: background 0.2s;
}

.safedigHeroCtaLink:hover .safedigHeroCta {
  background: #e5b520;
}



/* Helpful Links */
.safedigHelpfulLinksWrapper {
  width: 100%;
  text-align: center;
  margin-top: 50px;
  padding: 0 5%;
  position: relative;
}

.safedigHelpfulLinksHeader {
  position: relative;
  z-index: 1;
  margin: 0 0 50px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 3px;
  color: #5a6d7d;
  text-transform: uppercase;
}

.safedigHelpfulLinksBand {
  position: absolute;
  left: 0;
  right: 0;
  top: 176px;
  bottom: 70px;
  background-color: #869eb8;
  z-index: 0;
}

.safedigHelpfulLinksCardsRow {
  position: relative;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding-bottom: 70px;
  gap: 0 5%;
}

.safedigHelpfulLinksCard {
  flex: 0 1 30%;
  min-width: 260px;
  text-align: left;
  position: relative;
  background: #fff;
  box-shadow: 0 4px 20px rgba(45, 75, 108, 0.25);
}

.safedigHelpfulLinksCard img {
  display: block;
  width: 100%;
  height: auto;
}

.safedigHelpfulLinksCard .cardInfoBox {
  padding: 20px;
  background-color: #fff;
  min-height: 220px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.safedigCardLeftMargin {
  margin-left: 0;
}

.cardInfoBox h2 {
  font-size: 20px;
  font-weight: 700;
  color: #5a6d7d;
}

.cardInfoBox span {
  font-size: 15px;
  color: #6b7280;
  flex: 1;
}

.safedigHelpfulLinksCard .buttonContainer {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  text-align: center;
  margin-top: auto;
  padding-top: 16px;
}

.safedigHelpfulLinksCard .safedigButton {
  color: #2d4b6c;
  background-color: #edcd00;
  font-weight: 700;
  padding: 8px 16px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  cursor: pointer;
  border-radius: 2px;
}

.safedigHelpfulLinksCard .safedigButton:hover {
  opacity: 0.95;
}

/* Footer */
.siteFooter.footerOffWhite.safedigFooter {
  background: #e8e8e8;
  height: auto;
}

.safedigFooterMargin {
  margin-top: -120px;
  position: relative;
  z-index: 1;
}

.safedigLanding .safedigCustomFooter {
  padding: 100px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.safedigCustomFooter img {
  height: 60px;
  width: auto;
}

.safedigCustomFooter .copyright {
  color: #b8b8b8;
  margin-left: 32px;
  font-size: 14px;
  margin-bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .safedigProfessionalsHeroTitle {
    font-size: 1.75rem;
  }

  .safedigProfessionalsHeroSubtitle {
    font-size: 1rem;
  }


  .safedigHelpfulLinksWrapper {
    margin-top: 48px;
    padding: 0 3%;
  }

  .safedigHelpfulLinksHeader {
    font-size: 19px;
    letter-spacing: 2px;
    margin-bottom: 32px;
  }

  .safedigHelpfulLinksBand {
    top: 128px;
    bottom: 30px;
  }

  .safedigHelpfulLinksCardsRow {
    padding-bottom: 36px;
  }

  .safedigHelpfulLinksCard {
    flex: 0 1 48%;
    min-width: 0;
  }

  .safedigHelpfulLinksCard .cardInfoBox {
    min-height: 200px;
  }

  .safedigFooterMargin {
    margin-top: -80px;
  }

  .safedigLanding .safedigCustomFooter {
    padding: 80px 24px;
    flex-direction: column;
    text-align: center;
  }

  .safedigCustomFooter .copyright {
    margin-left: 0;
    margin-top: 16px;
  }
}

@media (max-width: 576px) {
  .safedig-professionals :deep(.content) {
    padding: 0 13px;
  }

  .safedigProfessionalsHero {
    min-height: 320px;
  }

  .safedigProfessionalsHeroTitle {
    font-size: 1.5rem;
  }

  .safedigProfessionalsHeroSubtitle {
    font-size: 0.9375rem;
  }

  .safedigHelpfulLinksWrapper {
    margin-top: 32px;
    padding: 0 13px;
  }

  .safedigHelpfulLinksHeader {
    font-size: 16px;
    margin-bottom: 24px;
  }

  .safedigHelpfulLinksBand {
    top: 96px;
    bottom: 40px;
  }

  .safedigHelpfulLinksCardsRow {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding-bottom: 32px;
  }

  .safedigHelpfulLinksCard {
    flex: 0 0 auto;
    width: 100%;
    max-width: 360px;
    min-width: 0;
  }

  .safedigHelpfulLinksCard .cardInfoBox {
    min-height: auto;
    padding: 16px;
  }

  .cardInfoBox h2 {
    font-size: 18px;
  }

  .cardInfoBox span {
    font-size: 14px;
  }

  .safedigFooterMargin {
    margin-top: -60px;
  }

  .safedigLanding .safedigCustomFooter {
    padding: 60px 16px;
  }

  .safedigCustomFooter img {
    height: 40px;
  }

  .safedigCustomFooter .copyright {
    font-size: 12px;
  }
}
</style>
